import React, { useState, useEffect, FC, useCallback, useMemo, useRef } from 'react';
import { time } from 'uniqid';
import { Modal, Button, Stack, H2 } from '@manulife/mux';
import { get } from 'lodash';
import { MIL_SECOND_FACTOR, DAYJS_DIFF } from '@trip-ux/constants';
import { ReduxStore } from '@trip-ux/redux-store';
import { useSelector } from 'react-redux';
import { Progress } from '../Progress';
import { dayjs, interpolateTemplate } from '@trip-ux/common';
import { useAdAuth } from '@trip-ux/components';
import { useUserActivity } from './useUserActivity';

export interface IdleReminderPropsAdmin {
  countDownSeconds: number;
  refresh: () => void;
  isOpen?: boolean;
  labels: {
    timedOut: string;
    remainingTimeout: string;
    loginAgain: string;
    refresh: string;
    refreshProgress: string;
  };
}

const checkIfShouldClearStore = (sessionExpiration, shouldCheckSessionExpiry) =>
  (dayjs().isSameOrAfter(dayjs(sessionExpiration)) && shouldCheckSessionExpiry) || !shouldCheckSessionExpiry;

const checkIfShouldExpireSession = (isSessionExpired, countDown) => !isSessionExpired && countDown <= 0;

export const IdleReminderAdmin: FC<IdleReminderPropsAdmin> = ({
  countDownSeconds,
  refresh,
  labels,
  isOpen = false
}) => {
  const { sessionIsExpired, sessionExpiration, debounceInterval } = useSelector((store: ReduxStore) => ({
    sessionIsExpired: Boolean(get(store, 'keepAlive.err')),
    sessionExpiration: get(store, 'keepAlive.sessionExpiration'),
    debounceInterval: get(store, 'config.options.debounceInterval', 10000)
  }));
  const [countDown, setCountDown] = useState(countDownSeconds);
  const [showSpinner, setShowSpinner] = useState(false);
  const [isModalOpen, setModalOpen] = useState(isOpen);
  const [intervalTimer, setIntervalTimer] = useState(null);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [modalShowInterval, setModalShowInterval] = useState(null);
  const [disableStayHere, setDisableStayHere] = useState(false);

  // Use ref to store the latest refresh function to avoid recreating the callback
  const refreshRef = useRef(refresh);
  const hasMountedRef = useRef(false);
  const prevCountDownRef = useRef(countDownSeconds);
  refreshRef.current = refresh;

  const initiateInterval = () => {
    clearInterval(modalShowInterval);
    const intervalTimerRef = setInterval(() => {
      setCountDown(dayjs(sessionExpiration).diff(dayjs(), DAYJS_DIFF.SECONDS));
    }, MIL_SECOND_FACTOR);
    setIntervalTimer(intervalTimerRef);
  };

  useEffect(() => {
    isModalOpen && initiateInterval();
  }, [isModalOpen]);

  useEffect(() => {
    if (checkIfShouldExpireSession(sessionIsExpired, countDown)) {
      setIsSessionExpired(true);
      clearInterval(intervalTimer);
      if (prevCountDownRef.current > 0) {
        redirectToCiamLogin();
      }
    }
    prevCountDownRef.current = countDown;
  }, [countDown, intervalTimer, isSessionExpired, modalShowInterval]);

  useEffect(() => {
    setCountDown(countDownSeconds);
    setModalOpen(isOpen);
    clearInterval(intervalTimer);

    const showModalCountdown = setInterval(() => {
      if (dayjs(sessionExpiration).isSameOrBefore(dayjs().add(countDownSeconds, DAYJS_DIFF.SECONDS))) {
        setModalOpen(true);
        setDisableStayHere(false);
      }
    }, MIL_SECOND_FACTOR);
    setModalShowInterval(showModalCountdown);

    return () => {
      clearInterval(showModalCountdown);
    };
  }, [sessionExpiration]);

  const timerOn = countDown > 0;
  const adAuthHook = useAdAuth();

  const redirectToCiamLogin = () => {
    setModalOpen(false);
    setShowSpinner(true);
    adAuthHook?.logout?.();
  };

  const clearStore = (shouldCheckSessionExpiry = true) => {
    if (checkIfShouldClearStore(sessionExpiration, shouldCheckSessionExpiry)) {
      redirectToCiamLogin();
    }
  };

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    if (sessionIsExpired) {
      redirectToCiamLogin();
    }
  }, [sessionIsExpired]);

  const onClickStayhere = () => {
    setDisableStayHere(true);
    refreshRef.current();
  };

  // Create a stable refresh callback that doesn't depend on the refresh prop
  const memoizedRefresh = useCallback(() => {
    console.log('User activity detected - calling refresh');
    refreshRef.current();
  }, []);

  const activityEvents = useMemo(() => ['keydown', 'mousedown'], []);

  useUserActivity(memoizedRefresh, {
    debounceInterval,
    activityEvents,
    isSessionExpired: sessionIsExpired || isSessionExpired
  });

  const headerText = timerOn
    ? disableStayHere
      ? labels?.refreshProgress
      : interpolateTemplate(labels?.remainingTimeout, { timeRemaining: String(countDown) })
    : labels?.timedOut;
  return (
    <>
      {showSpinner && <Progress backdrop />}
      <Modal
        onClose={() => clearStore(false)}
        isOpen={isModalOpen}
        id={time(`session-`)}
        closeIconAriaLabel={labels?.loginAgain}
        shouldCloseOnBackdropClick={false}
        shouldCloseWithEscapeKey={false}
        aria-label="Idle Reminder"
        aria-labelledby="session-reminder-header"
        aria-describedby="session-reminder-content">
        <Stack as="div" gap="16px" align="center">
          <H2 id="session-reminder-header" data-testid="session-reminder-header" aria-live="polite" aria-atomic="true">
            {headerText}
          </H2>

          <Stack as="div" direction="row" id="session-reminder-content">
            {isSessionExpired && (
              <Button id="session-reminder-cancel" data-testid="session-reminder-cancel" onClick={() => clearStore(false)} variant="secondary">
                {labels?.loginAgain}
              </Button>
            )}
            {timerOn && (
              <Button id="session-reminder-refresh" data-testid="session-reminder-refresh" onClick={() => onClickStayhere()} variant="primary" disabled={disableStayHere}>
                {labels?.refresh}
              </Button>
            )}
          </Stack>
        </Stack>
      </Modal>
    </>
  );
};

IdleReminderAdmin.displayName = 'IdleReminderAdmin';
export default IdleReminderAdmin;
